import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ForwardToInboxOutlinedIcon from "@mui/icons-material/ForwardToInboxOutlined"
import MarkEmailUnreadIcon from "@mui/icons-material/MarkEmailUnreadOutlined"
import AccordionDetails from "@mui/material/AccordionDetails"
import AccordionSummary from "@mui/material/AccordionSummary"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import Divider from "@mui/material/Divider"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"
import { compareDesc } from "date-fns"
import { useEffect, useState } from "react"

import Column, {
  CenterEverythingColumn,
} from "../../../../../common/components/Column"
import Row from "../../../../../common/components/Row"
import { formatDateTime } from "../../../../../common/utils"
import { useSearchParamsState } from "../../../../../common/utils"
import BorderedGrayChip from "../../../components/BorderedGrayChip"
import { useGetTransactionInfo } from "../../../utils"
import { EVENT_TYPES } from "../../data"
import { GLOBAL_SEARCH_URL_PARAMS } from "../../data"
import {
  AccordionDetailsContent,
  AccordionDetailsTypography,
  AccordionSummaryContent,
  StyledAccordion,
} from "./Accordions"
import { performedBySubtitle } from "./PerformedByAttribution"
import TabColumn from "./TabColumn"
import TabContentColumn from "./TabContentColumn"
import TabHeaderRow from "./TabHeaderRow"
import ActivitySkeleton from "./components/ActivitySkeleton"
import { useNotificationUtils } from "./utils"

export default function EmailsTab({
  candidate,
  setCandidate,
  onEmailButtonClicked,
  isLoading = false,
}) {
  const emailEvents = reverseSortByCreatedDate(
    candidate.crm_events.filter(isEmailEvent)
  )
  const { expandedEventID, setExpandedEventID } = useURLBoundExpandedAccordion(
    candidate,
    setCandidate,
    emailEvents,
    GLOBAL_SEARCH_URL_PARAMS.emailTransaction
  )
  const { getHasUnreadTransactions, markEventTransactionsRead } =
    useNotificationUtils()

  function handleAccordionExpandChanged(event, isExpanded) {
    if (isExpanded) {
      setExpandedEventID(event.id)
      markEventTransactionsRead(candidate, setCandidate, event)
    } else {
      setExpandedEventID(null)
    }
  }

  return (
    <TabColumn>
      <TabHeaderRow title="Emails History">
        <Button
          variant="outlined"
          color="inherit"
          startIcon={<EmailOutlinedIcon />}
          onClick={onEmailButtonClicked}
          disabled={Boolean(candidate.is_dnc)}
        >
          Send Email
        </Button>
      </TabHeaderRow>
      <TabContentColumn>
        {isLoading ? (
          <ActivitySkeleton />
        ) : emailEvents.length === 0 ? (
          <CenterEverythingColumn>
            <Typography variant="body1" color="text.secondary">
              No emails sent yet.
            </Typography>
          </CenterEverythingColumn>
        ) : (
          emailEvents.map((event) => (
            <StyledAccordion
              expanded={event.id === expandedEventID}
              onChange={(e, isExpanded) =>
                handleAccordionExpandChanged(event, isExpanded)
              }
              key={event.id}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <AccordionSummaryContent
                  Icon={getEmailEventIcon(event)}
                  accordionTitle={
                    <Column>
                      <AccordionCounterTitle
                        title={getEmailEventTitle(event)}
                        count={(event.transactions || []).length}
                        hasNew={getHasUnreadTransactions(event)}
                      />
                    </Column>
                  }
                  subtitle={[
                    performedBySubtitle(event),
                    event.transactions?.[0]?.subject || event.campaign?.subject,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                  dateStr={event.created}
                />
              </AccordionSummary>
              <AccordionDetails>
                <AccordionDetailsContent>
                  <EventThread event={event} candidate={candidate} />
                </AccordionDetailsContent>
              </AccordionDetails>
            </StyledAccordion>
          ))
        )}
      </TabContentColumn>
    </TabColumn>
  )
}

function EventThread({ event, candidate }) {
  const transactions = reverseSortByCreatedDate(event.transactions || [])
  const { getSenderReceiverNames } = useGetTransactionInfo()

  function processFirstTransaction(firstTransaction) {
    const templateElement = document.createElement("template")
    templateElement.innerHTML = firstTransaction.body
    const bodyInnerElements =
      templateElement.content.querySelectorAll("div.body > *")
    // only EmailSendEvent types have body content
    if (bodyInnerElements.length > 0) {
      // step 2: collect multiple children into single html string
      const bodyInnerElementsWrapper = document.createElement("div")
      bodyInnerElements.forEach((elem) =>
        bodyInnerElementsWrapper.appendChild(elem)
      )
      return bodyInnerElementsWrapper.innerHTML
    }
    return firstTransaction.body
  }

  return (
    <Column width="100%">
      {transactions.length === 0 ? (
        <EmailContentTypography
          dangerouslySetInnerHTML={{
            __html:
              event.type === EVENT_TYPES.email
                ? event.campaign?.rendered_body
                : event.type === EVENT_TYPES.directEmail
                ? event.rendered_body
                : "",
          }}
        />
      ) : (
        transactions.map((transaction, i) => (
          <Column gap={2}>
            <Column gap={0.5}>
              <HeaderNameValue name="Subject" value={transaction.subject} />
              <HeaderNameValue
                name="From"
                value={
                  getSenderReceiverNames(transaction, candidate).senderName
                }
              />
              <HeaderNameValue
                name="Date"
                value={formatDateTime(transaction.date)}
              />
            </Column>
            <EmailContentTypography
              key={i}
              dangerouslySetInnerHTML={{
                __html:
                  i === 0
                    ? processFirstTransaction(transaction)
                    : transaction.body,
              }}
            />
            {shouldShowTransactionDivider(transactions, i) ? (
              <Divider sx={{ mb: 2 }} />
            ) : null}
          </Column>
        ))
      )}
    </Column>
  )
}

export function HeaderNameValue({ name, value }) {
  return (
    <Row gap={1}>
      <Typography variant="body1" fontWeight={600}>
        {name}:
      </Typography>
      <Typography variant="body1">{value}</Typography>
    </Row>
  )
}

const EmailContentTypography = styled((props) => (
  <AccordionDetailsTypography component="div" {...props} />
))(() => ({
  whiteSpace: "pre-wrap",
  "& *": {
    marginBlock: 0,
  },
}))

export function getEmailReceiveEventSubject(emailReceiveEvent) {
  if (emailReceiveEvent.transactions) {
    return emailReceiveEvent.transactions[0]?.subject
  } else if (emailReceiveEvent.campaign) {
    return emailReceiveEvent.campaign.subject
  }
  return emailReceiveEvent.subject
}

export function getEmailEventIcon(crmEvent) {
  switch (crmEvent.type) {
    case EVENT_TYPES.email:
      return <EmailOutlinedIcon />
    case EVENT_TYPES.directEmail:
      return <ForwardToInboxOutlinedIcon />
    case EVENT_TYPES.emailReceiveEvent:
      return <MarkEmailUnreadIcon />
    default:
      return null
  }
}

export function getEmailEventTitle(crmEvent) {
  switch (crmEvent.type) {
    case EVENT_TYPES.email:
    case EVENT_TYPES.directEmail:
      return "Email sent"
    case EVENT_TYPES.emailReceiveEvent:
      return "Email received"
    default:
      return ""
  }
}

const EMAIL_EVENT_TYPES = [
  EVENT_TYPES.email,
  EVENT_TYPES.directEmail,
  EVENT_TYPES.emailReceiveEvent,
]

export function useURLBoundExpandedAccordion(
  candidate,
  setCandidate,
  events,
  globalSearchURLParamName
) {
  const [expandedEventID, setExpandedEventID] = useState(null)
  const [expandedTransactionID] = useSearchParamsState(globalSearchURLParamName)
  const { markEventTransactionsRead } = useNotificationUtils()

  useEffect(
    () => {
      if (expandedTransactionID) {
        const eventWithTransactionInURLParam = events.find((event) =>
          event.transactions.find(
            (tx) => tx.id === parseInt(expandedTransactionID)
          )
        )
        if (eventWithTransactionInURLParam) {
          setExpandedEventID(eventWithTransactionInURLParam.id)
          markEventTransactionsRead(
            candidate,
            setCandidate,
            eventWithTransactionInURLParam
          )
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [candidate, expandedTransactionID]
  )

  return { expandedEventID, setExpandedEventID }
}

export function shouldShowTransactionDivider(transactions, i) {
  return i < transactions.length - 1 && transactions.length > 1
}

const InlineChipMixin = {
  height: "18px",
  fontSize: "11px",
  "& .MuiChip-label": {
    paddingInline: "10px",
    lineHeight: 1,
  },
}

const CounterChip = styled((props) => (
  <BorderedGrayChip size="small" {...props} />
))(() => InlineChipMixin)

export const HasNewChip = styled((props) => (
  <Chip color="secondary" {...props} />
))(() => ({
  ...InlineChipMixin,
  "& .MuiChip-label": {
    ...InlineChipMixin["& .MuiChip-label"],
    paddingInline: "7px",
  },
}))

export function AccordionCounterTitle({ title, count, hasNew }) {
  return (
    <Row sx={{ alignItems: "center", gap: 1 }}>
      <span>{title}</span>
      <CounterChip label={count} />
      {hasNew ? <HasNewChip label="new" /> : null}
    </Row>
  )
}

export function reverseSortByCreatedDate(itemsWithCreatedField) {
  return itemsWithCreatedField
    .slice()
    .sort((item1, item2) =>
      compareDesc(new Date(item1.created), new Date(item2.created))
    )
}

export function isEmailEvent(event) {
  return EMAIL_EVENT_TYPES.indexOf(event.type) > -1
}
